// 登录页 —— 邮箱+密码 / Google OAuth / 跳转注册。

import { useState } from 'react';
import { login, oauthStartUrl } from '../lib/api';
import { writeSession } from '../lib/storage';
import { navigate } from '../lib/route';

export function Login(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await login(email, password);
      if ('requires_totp' in res) {
        setErr('需要 TOTP 二次验证（暂未在 SPA 中接入）');
        return;
      }
      writeSession({ token: res.jwt, email, role: 'owner' });
      navigate('sites');
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : '登录失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={brandStyle}>
          <span style={logoBoxStyle}>🛡️</span>
          <span>Captcha SaaS</span>
        </div>
        <h1 style={titleStyle}>登录</h1>
        <p style={subtitleStyle}>欢迎回来，请使用账号登录控制台</p>

        <form onSubmit={onSubmit} style={{ marginTop: 24 }}>
          <Field label="邮箱">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={inputStyle}
            />
          </Field>
          <Field label="密码">
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="至少 12 位"
              style={inputStyle}
            />
          </Field>
          <button type="submit" disabled={busy} style={primaryBtn}>
            {busy ? '登录中…' : '登录'}
          </button>
          {err && <div style={errorBoxStyle}>{err}</div>}
        </form>

        <div style={dividerStyle}>
          <span style={dividerLineStyle} />
          <span style={dividerTextStyle}>或</span>
          <span style={dividerLineStyle} />
        </div>

        <a href={oauthStartUrl('google')} style={googleBtn}>
          <GoogleIcon />
          使用 Google 登录
        </a>

        <div style={footerLinksStyle}>
          没有账号？
          <a
            href="#/register"
            style={linkStyle}
            onClick={(e) => {
              e.preventDefault();
              navigate('register');
            }}
          >
            立即注册
          </a>
        </div>
      </div>
    </div>
  );
}

function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={labelStyle}>{props.label}</div>
      {props.children}
    </label>
  );
}

function GoogleIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.93 8.93 0 0 0 9 0 9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

// ---- styles ----
const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background:
    'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
};
const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 400,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  padding: 32,
  boxShadow: '0 24px 50px -28px rgba(15,23,42,.18)',
};
const brandStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontWeight: 700,
  fontSize: 16,
  marginBottom: 24,
  color: '#0f172a',
};
const logoBoxStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 8,
  background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
  display: 'inline-grid',
  placeItems: 'center',
  fontSize: 16,
};
const titleStyle: React.CSSProperties = { fontSize: 24, margin: 0, color: '#0f172a' };
const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#64748b',
  margin: '8px 0 0',
};
const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#475569',
  marginBottom: 6,
  fontWeight: 500,
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border-color .15s, box-shadow .15s',
};
const primaryBtn: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
  color: '#fff',
  border: 0,
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  marginTop: 4,
};
const errorBoxStyle: React.CSSProperties = {
  marginTop: 12,
  padding: 10,
  fontSize: 13,
  color: '#991b1b',
  background: '#fee2e2',
  borderRadius: 8,
  border: '1px solid #fecaca',
};
const dividerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '24px 0',
};
const dividerLineStyle: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: '#e2e8f0',
};
const dividerTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#94a3b8',
};
const googleBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  width: '100%',
  padding: '11px 14px',
  background: '#fff',
  color: '#334155',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  textDecoration: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box',
};
const footerLinksStyle: React.CSSProperties = {
  marginTop: 20,
  fontSize: 13,
  color: '#64748b',
  textAlign: 'center',
};
const linkStyle: React.CSSProperties = {
  color: '#2563eb',
  textDecoration: 'none',
  marginLeft: 4,
  fontWeight: 500,
};
